import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Outlet } from 'react-router';

import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AuthProvider } from '@/features/auth/auth-provider';
import { ContentLocaleProvider } from '@/features/locales/content-locale';

import { createQueryClient } from './query-client';
import { ThemeProvider } from './theme';

/**
 * Cross-cutting providers, mounted as a pathless route so the auth layer can use
 * router hooks.
 *
 * The query client is created in state rather than at module scope so each test
 * render — and each Fast Refresh cycle — gets a clean cache instead of
 * inheriting another test's data.
 */
export function AppProviders({ children }: { children?: React.ReactNode }) {
  const [queryClient] = useState<QueryClient>(() => createQueryClient());

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <ContentLocaleProvider>
            <TooltipProvider delayDuration={300}>
              {children ?? <Outlet />}
              <Toaster position="bottom-right" closeButton richColors />
            </TooltipProvider>
          </ContentLocaleProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
